import api from "../lib/api";

// OpenLayers
import OlSourceOSM from "ol/source/OSM";
import OlSourceVector from "ol/source/Vector";
import OlSourceTileWMS from "ol/source/TileWMS";
import OlSourceImageWMS from "ol/source/ImageWMS";
import OlSourceWMTS, { optionsFromCapabilities, Options } from "ol/source/WMTS";
import OlWMTSCapabilities from "ol/format/WMTSCapabilities";
import OlSourceXYZ from "ol/source/XYZ";

import OlWMTSTileGrid from "ol/tilegrid/WMTS";

import OlLayerTile from "ol/layer/Tile";
import OlLayerImage from "ol/layer/Image";
import OlLayerVector from "ol/layer/Vector";
import OlLayerGroup from "ol/layer/Group";

import OlCollection from "ol/Collection";
import OlBaseLayer from "ol/layer/Base";
import OlTileSource from "ol/source/Tile";

import OlLineString from "ol/geom/LineString";

import OlFormatWKT from "ol/format/WKT";
import OlFeature from "ol/Feature";

import { transform } from "ol/proj";

import {
  BaseLayerDefinition,
  BaseLayersData,
  BaseLayerSourceType,
  BaseLayerLayerType,
  DefaultMapData,
  IMapService,
  LayerDefinition,
  DataLayerSourceType,
  DataLayerLayerType,
  RoutingInfo,
  RouteInfo,
  RouteWarning
} from "@/@types/services/mapService";
import { TFunction } from "i18next";
import { Geometry } from "ol/geom";
import { DCRecord } from "@/@types/lib/dataController";

import dguWMTSCapabilitiesTK25 from "@/data/dgu_wmts_capabilities_dof_hok_tk.xml";
import dguWMTSCapabilitiesDOF5 from "@/data/dgu_wmts_capabilities_dof_2021_2022.xml";

const gs_url = process.env.REACT_APP_GEOSERVERPATH;

const mapService: IMapService = {
  getBaseLayers,
  getLayers,
  getDefaultData,
  getRoute,
};

function getDefaultData(mapId: number) {
  const apiInstance = new api();
  return apiInstance.Call(`maps/${mapId}/settings`, "get").then((resp) => {
    if (resp && resp.data) {
      return resp.data as DefaultMapData;
    }
    return null;
  });
}
function getBaseLayers(mapId: number, t: TFunction): Promise<OlLayerGroup> {
  const apiInstance = new api();
  return apiInstance.Call(`maps/${mapId}/baselayers`, "get").then((resp) => {
    if (resp.success) {
      const baselayerDefinitions = resp.data as BaseLayersData;
      const baselayers = prepareBaseLayers(baselayerDefinitions, t, mapId);
      return new OlLayerGroup({
        layers: baselayers,
      });
    } else {
      return new OlLayerGroup({
        layers: [],
      });
    }
  });
}

function getLayers(mapId: number): Promise<OlCollection<OlBaseLayer>> {
  const apiInstance = new api();
  return apiInstance.Call(`maps/${mapId}/layers`, "get").then((resp) => {
    if (resp.success) {
      const layerDefinitions = resp.data as Array<LayerDefinition>;
      const layers = prepareLayers(layerDefinitions);
      const coll = new OlCollection(layers);
      return coll;
    } else {
      return new OlCollection();
    }
  });
}

function getRoute(feat: OlFeature<OlLineString>): Promise<RoutingInfo | null> {
  const geom = feat.getGeometry();
  if (geom) {
    const p1 = transform(geom.getFirstCoordinate(), "EPSG:3857", "EPSG:3765");
    const p2 = transform(geom.getLastCoordinate(), "EPSG:3857", "EPSG:3765");
    // const wktFormatter = new OlFormatWKT();
    // const wkt = wktFormatter.writeFeature(feat, {
    //   dataProjection: "EPSG:3857",
    //   featureProjection: "EPSG:3857"
    // } )
    const apiInstance = new api();
    const data = { p1: `${p1[0]},${p1[1]}`, p2: `${p2[0]},${p2[1]}` };

    const coordsHTRS = geom
      .getCoordinates()
      .map((x) => transform(x, "EPSG:3857", "EPSG:3765"));
    let dataAllPoints = {};
    coordsHTRS.forEach((x, i) => {
      //@ts-ignore
      dataAllPoints[`p${i + 1}`] = `${x[0]},${x[1]}`;
    });

    return apiInstance
      .Call("routing/route", "post", dataAllPoints)
      .then((resp) => {
        if (resp.success && resp.data) {
          if (Array.isArray(resp.data)) {
            const records = resp.data as DCRecord[];
            let routeInfoArray: RouteInfo[] = [];
            records
              .filter(
                (rec) =>
                  rec.ruta_geom !== undefined &&
                  // @ts-ignore
                  rec.ruta_geom?.substring(2) !== undefined
              )
              .forEach((rec) => {
                //@ts-ignore
                const duljina = rec.total_length;
                //@ts-ignore
                const wkb = rec.ruta_geom ? rec.ruta_geom.substring(2) : null;

                const routeInfo: RouteInfo = {
                  id: 1,
                  // @ts-ignore
                  code: rec?.route_id,
                  total_length: duljina as number,
                  total_time: rec?.total_time_hhmm as string,
                  total_time_min: rec?.total_time_min as number,
                  min_h: rec?.min_h as number,
                  max_h: rec?.max_h as number,
                  total_ascent: rec?.total_ascent as number,
                  total_descent: rec?.total_descent as number,
                  wkb: wkb,
                  hash: rec.ruta_hash,
                  segments: rec?.segments,
                  points: rec?.points,
                  warnings_msp: rec?.warnings_msp as RouteWarning,
                  warnings_podrucja: rec?.warnings_podrucja as RouteWarning
                };

                routeInfoArray = routeInfoArray.concat(routeInfo);
              });
            if (routeInfoArray.length > 0) {
              return Promise.resolve(routeInfoArray);
            } else {
              return Promise.reject(null);
            }
          } else {
            return Promise.reject(null);
          }
        } else {
          return Promise.reject(null);
        }
      });
  } else {
    return Promise.reject(null);
  }
}

const getBaseLayerSource = (def: BaseLayerDefinition) => {
  switch (def.layer_source) {
    case BaseLayerSourceType.ImageWMS:
      return new OlSourceImageWMS({
        url: def.url ? def.url : gs_url,
        params: {
          LAYERS: def.layer,
          FORMAT: def.format,
        },
      });
    case BaseLayerSourceType.TileWMS:
      return new OlSourceTileWMS({
        url: def.url ? def.url : gs_url,
        projection: def.projection,
        params: {
          LAYERS: def.layer,
          FORMAT: def.format,
          TILED: def.tiled ? true : false,
        },
      });
    case BaseLayerSourceType.WMTS:
      if (def.url.indexOf('https://geoportal.dgu.hr') === 0) {
        const parser = new OlWMTSCapabilities();
        let capabilities = "";
        if (def.url.indexOf('https://geoportal.dgu.hr/services/sla/dof_hok_tk/wmts') === 0) {
          capabilities = parser.read(dguWMTSCapabilitiesTK25);
        } else if (def.url.indexOf('https://geoportal.dgu.hr/services/auth/orthophoto_2021_2022/wmts') === 0) {
          capabilities = parser.read(dguWMTSCapabilitiesDOF5);
        } else {
          capabilities = parser.read(dguWMTSCapabilitiesTK25);
        }
        const wmtsOptions = optionsFromCapabilities(capabilities, {
          url: def.url,
          layer: def.layer,
          matrixSet: def.projection,
          }) as Options;
        return new OlSourceWMTS(wmtsOptions);
      } else {
        const wmtsOptions = {
          format: def.format, // Odaberi hibridni format (default image/jpeg)
          layer: def.layer ? def.layer : "",
          matrixSet: "EPSG:900913", // Ime gridseta
          style: "",
          tileGrid: createEPSG3857Grid(), // Gore kreirani gridset
          url: def.url,
        };
        return new OlSourceWMTS(wmtsOptions);
      }
    case BaseLayerSourceType.OSM:
      return new OlSourceOSM();
    default:
      return new OlSourceOSM();
  }
};

const getBaseLayerLayer = (def: BaseLayerDefinition, options: any) => {
  switch (def.layer_type) {
    case BaseLayerLayerType.ImageLayer:
      return new OlLayerImage(options);
    case BaseLayerLayerType.TileLayer:
      return new OlLayerTile(options);
  }
};

function prepareBaseLayers(
  defs: Array<BaseLayerDefinition>,
  t: TFunction,
  mapId: number
): Array<OlBaseLayer> {
  const layers: Array<OlBaseLayer> = [];
  let first = true;

  // Retrieve and parse localStorage data with error handling
  let parsedMapBaseLayers: { [key: number]: string } = {};

  try {
    const mapBaseLayers = localStorage.getItem("mapbaselayers");
    if (mapBaseLayers) {
      parsedMapBaseLayers = JSON.parse(mapBaseLayers);
    }
  } catch (error) {
    console.error("Error parsing mapbaselayers from localStorage:", error);
    // Fallback to an empty object if parsing fails
    parsedMapBaseLayers = {};
  }

  if (Array.isArray(defs)) {
    defs.forEach((def) => {
      const options = {
        source: getBaseLayerSource(def),
      };

      const layer = getBaseLayerLayer(def, options);
      layer.set("id", "base-" + def.id.toString());
      layer.set("title", t(def.ttoken) as string);
      layer.set("title_ttoken", def.ttoken);
      layer.set("type", "base");
      layer.set("code", def.code);
      layer.set("baseLayer", true);
      layer.set("preview", "data:image/jpg;base64," + def.preview_base64);

      if (parsedMapBaseLayers[mapId] === undefined) {
        if (first) {
          layer.setVisible(true);
          first = false;
        } else {
          layer.setVisible(false);
        }
      } else if (parsedMapBaseLayers[mapId] === def.code) {
        layer.setVisible(true);
      } else {
        layer.setVisible(false);
      }

      layers.push(layer);
    });
  }

  return layers;
}

function getDataLayerSource(def: LayerDefinition) {
  switch (def.layer_source) {
    case DataLayerSourceType.GROUP:
      return null;
    case DataLayerSourceType.TileWMS:
      return new OlSourceTileWMS({
        url: def.url ? def.url : gs_url,
        params: {
          LAYERS: def.layer,
          FORMAT: def.format,
          TILED: def.tiled ? true : false,
        },
      });
    case DataLayerSourceType.ImageWMS:
      const imageWMSoptions = {
        url: def.url ? def.url : gs_url,
        params: {
          LAYERS: def.layer ? def.layer : "",
          FORMAT: def.format,
        },
      };
      return new OlSourceImageWMS(imageWMSoptions);
    case DataLayerSourceType.WMTS:
      const wmtsOptions = {
        format: def.format, // Odaberi hibridni format (default image/jpeg)
        layer: def.layer ? def.layer : "",
        matrixSet: "EPSG:900913", // Ime gridseta
        style: "",
        tileGrid: createEPSG3857Grid(), // Gore kreirani gridset
        url: def.url,
      };
      return new OlSourceWMTS(wmtsOptions);
    case DataLayerSourceType.TMS:
      return new OlSourceXYZ({
        url: def.url,
      });
    case DataLayerSourceType.OSM:
      return new OlSourceOSM();
    case DataLayerSourceType.VectorSource:
      return new OlSourceVector({});
    default:
      return null;
  }
}

function getDataLayerLayer(def: LayerDefinition): OlBaseLayer {
  switch (def.layer_type) {
    case DataLayerLayerType.GROUP:
      let options = {
        fold: "open",
        visible: def.visible,
        openInLayerSwitcher: true,
      };
      if (
        def.ui_switcher_options !== "" &&
        def.ui_switcher_options !== undefined
      ) {
        Object.assign(options, JSON.parse(def.ui_switcher_options));
      }
      return new OlLayerGroup(options);
    case DataLayerLayerType.TileLayer:
      return new OlLayerTile({
        source: getDataLayerSource(def) as OlSourceTileWMS,
        visible: def.visible,
      });
    case DataLayerLayerType.ImageLayer:
      return new OlLayerImage({
        source: getDataLayerSource(def) as OlSourceImageWMS,
        visible: def.visible,
      });
    case DataLayerLayerType.VectorLayer:
      return new OlLayerVector({
        source: getDataLayerSource(def) as OlSourceVector<OlFeature<Geometry>>,
        visible: def.visible,
      });
  }
}

function flatDeep(arr: Array<OlBaseLayer>, d: number = 1): Array<OlBaseLayer> {
  return d > 0
    ? arr.reduce(
        (acc: Array<OlBaseLayer>, val) =>
          acc.concat(
            val instanceof OlLayerGroup && val.getLayers().getArray().length > 0
              ? ([val] as Array<OlBaseLayer>).concat(
                  flatDeep(val.getLayers().getArray(), d - 1)
                )
              : val
          ),
        []
      )
    : arr.slice();
}

function prepareLayers(defs: Array<LayerDefinition>): Array<OlBaseLayer> {
  const layers: Array<OlBaseLayer> = [];

  if (Array.isArray(defs)) {
    defs.forEach((def: LayerDefinition) => {
      const layer = getDataLayerLayer(def);
      if (layer) {
        layer.set("id", "app-" + def.code.toString());
        layer.set("title", def.ttoken);
        layer.set("type", "app-defined");
        layer.set("layer", def.layer);
        layer.set("styles", def.styles);
        layer.set("query", def.can_query);
        layer.set("call_group", def.call_group);
        layer.set("format", def.format);
        layer.set("tiled", def.tiled);
        layer.set("use_cache", def.use_cache);
        layer.set("z_index", def.z_index);
        if (def.z_index) {
          layer.setZIndex(def.z_index);
        }
        layer.set("url", def.url);
        layer.set("extent", def.extent);
        layer.set(
          'visible_min_zoom',
          def.visible_min_zoom ? def.visible_min_zoom : undefined
        );
        layer.set(
          'visible_max_zoom',
          def.visible_max_zoom ? def.visible_max_zoom : undefined
        );

        if (
          def.parent_layer_code === null ||
          def.parent_layer_code === undefined
        ) {
          layers.push(layer);
        } else {
          const parentCode = def.parent_layer_code;
          const flatLayers: Array<OlBaseLayer> = layers
            ? flatDeep(layers, 3)
            : [];
          const parentLayer = flatLayers.find(
            (x) => x.get("id") === "app-" + parentCode
          ) as OlLayerGroup;
          if (parentLayer) {
            parentLayer.setLayers(
              new OlCollection([...parentLayer.getLayers().getArray(), layer])
            );
          }
        }
      }
    });
  }

  return layers;
}

function createEPSG3857Grid() {
  // Kreiraj grid za EPSG:900913/EPSG:3857
  const maxZoom = 22;
  const resolutions = new Array(maxZoom);
  const matrixIds = new Array(maxZoom);
  for (let z = 0; z <= maxZoom; z++) {
    resolutions[z] = 40075016 / 256 / Math.pow(2, z);
    matrixIds[z] = "EPSG:900913:" + z; // Capabilities/Contents/TileMatrixSet/TileMatrix/ows:Identifier
  }
  const grid = new OlWMTSTileGrid({
    origin: [-20037508, 20037508], // Capabilities/Contents/TileMatrixSet/TileMatrix/TopLeftCorner
    resolutions: resolutions,
    matrixIds: matrixIds,
    extent: [-20037508.34, -20037508.34, 20037508.34, 20037508.34],
  });

  return grid;
}

export default mapService;
